import * as store from './store';
import * as events from './event-bus';
import CryptoJS from 'crypto-js';
import { CACHE_CONFIG } from './constants';
import { injectData } from './config-helpers';

const defaults = {
	type: 'session',
	expires: 30 // in minutes
};
const inverseType = {session: 'local', local: 'session'};
const sStore = window.sessionStorage;
const lStore = window.localStorage;
const ns = 'SK_';

export const set = function (key, data, config) {
	const cfg = Object.assign({}, defaults, config, {data});
	clearUnusedKeys(`${cfg.type}Storage`, key);
	if (typeof cfg.expires === 'number' && isFinite(cfg.expires)) {
		const minToMs = 60 * 1000;
		cfg.expires = Date.now() + (cfg.expires * minToMs);
	} else {
		cfg.expires = null;
	}
	try {
		window[`${cfg.type}Storage`].setItem(`${ns}${key}`, JSON.stringify(cfg));
		window[`${inverseType[cfg.type]}Storage`].removeItem(`${ns}${key}`);
	} catch(e) {
		/* eslint-disable no-console */
		console.error('Sidekick error: ', e);
	}
};

export const get = function (key) {
	const raw = sStore.getItem(`${ns}${key}`) || lStore.getItem(`${ns}${key}`) || null;
	if (raw == null) { return null; }
	const json = JSON.parse(raw);

	// if the data has expired, return null
	if (json.expires != null && json.expires < Date.now()) { return null; }
	return json.data;
};


export const getCachedData = function (key) {
	const raw = sStore.getItem(`${ns}${key}`) || lStore.getItem(`${ns}${key}`) || null;
	if (raw == null) { return null; }
	const json = JSON.parse(raw);
	return json.data;
};


// cleanup the expired persistence values when we first load
store.onValueOnce('sk-config', function (cfg) {
	const {cleanCache} = cfg;

	// clean out anything that is expired
	[lStore, sStore].forEach(function (storage) {
		// spin all the items in local/session storage
		var i = storage.length;
		for (; i-- ;) {
			// get the key to inspect
			const key = storage.key(i);
			// if it's not a SideKick value, don't mess with it
			if (!key.startsWith(ns)) { continue; }
			// can we get data for this key?
			const data = get(key.slice(ns.length));
			// if the data is null or we are in a clearing cache state, remove it
			if (data == null || cleanCache) { storage.removeItem(key); }
		}
	});
});

/**
 * This method clears all the unused Sidekick data, that starts with {@link ns} @param {String} key[0] in the storage.
 * @param {String} storageType type of storage, ex: localStorage, sessionStorage
 * @param {String} key the current key which is to be set. It contains the flId and sessionId at index [1] & [2] when split by '-'
 */
function clearUnusedKeys(storageType, key) {
	const keyData = key.split('-');
	if(keyData.length > 2) {
		const existingKeys = Object.keys(window[storageType]).filter(item => item.startsWith(`${ns}${keyData[0]}`));
		const unusedKeys = existingKeys.filter(item => !item.endsWith(`-${keyData[1]}-${keyData[2]}`));
		if(unusedKeys.length > 0) {
			for(const unusedKey of unusedKeys) {
				window[storageType].removeItem(unusedKey);
			}
		}
	}
}
// ---------------- Config Cache (closure-based) ----------------
let _isInitialized = false;
let _lastFetchParams = null;
let _isRefreshing = false;

/**
 * Setup event listeners for cache invalidation
 */
function setupInvalidationListeners() {
	if (_isInitialized) {return;}
	_isInitialized = true;

	events.on('header.user-info.role', (role) => {
        refreshCache({ reason: 'role-change', role });
    });
    events.on('header.org-switcher.change', (org) => {
        clearCache({ reason: 'org-change', org });
    });
}

/**
 * Safely retrieve nested property value from object
 * @param {Object} obj - The object to query
 * @param {Array<string>} paths - Array of paths to try in order
 * @param {string} fallback - Fallback value if all paths fail
 * @returns {string} Value at path or fallback
 */
function getNestedValueOrFallback(obj, paths, fallback) {
	if (!obj || !Array.isArray(paths)) {return fallback;}
	for (const path of paths) {
		if (!path) {continue;}
		const keys = path.split('.');
		let value = obj;
		let found = true;
		for (const key of keys) {
			value = value && typeof value === 'object' ? value[key] : undefined;
			if (value === undefined) {
				found = false;
				break;
			}
		}
		if (found && value !== null) {return value;}
	}
	return fallback;
}

/**
 * Create cache key from passport data
 * @param {Object} passportData - Passport authentication data
 * @returns {string|null} Cache key or null if data unavailable
 */
function createPassportCacheKey(passportData) {
	if (!passportData || !passportData.userInfo) {return null;}
	const orgId = getNestedValueOrFallback(passportData, ['userInfo.domain.id'], 'unknown-org');
	const personId = getNestedValueOrFallback(passportData, ['userInfo.person.id'], 'unknown-person');
	const roleName = getNestedValueOrFallback(passportData, ['userInfo.role.name'], 'unknown-role');
	return `CONFIG_${orgId}_${personId}_${roleName}`;
}

/**
 * Create cache key from IDM data
 * @param {Object} idmData - IDM authentication data
 * @returns {string|null} Cache key or null if data unavailable
 */
function createIdmCacheKey(idmData) {
	if (!idmData) {return null;}
	const orgId = getNestedValueOrFallback(idmData,
		['userContext.OrganizationId', 'session.State.currentOrg'],
		'unknown-org'
	);
	const profileId = getNestedValueOrFallback(idmData, ['user.profileId'], 'unknown-profile');
	const sessionId = getNestedValueOrFallback(idmData, ['session.Id'], 'unknown-session');
	return `CONFIG_${orgId}_${profileId}_${sessionId}`;
}

/**
 * Generate endpoint hash for fallback cache key
 * @param {string} endpoint - The config API endpoint
 * @returns {string} 12-character MD5 hash
 */
function generateEndpointHash(endpoint) {
	return CryptoJS.MD5(endpoint || '').toString().substring(0, 12);
}

/**
 * Create a unique cache key based on user profile and endpoint
 * Attempts to use passport data, falls back to IDM data, and finally uses endpoint hash
 * @param {string} endpoint - The config API endpoint
 * @returns {string} Unique cache key
 */
function createCacheKey(endpoint) {
	const passportData = store.get('passport-data');
	const idmData = store.get('idm-data');

	const passportKey = createPassportCacheKey(passportData);
	if (passportKey) {return passportKey;}

	const idmKey = createIdmCacheKey(idmData);
	if (idmKey) {return idmKey;}

	return `CONFIG_${generateEndpointHash(endpoint)}`;
}

/**
 * Check if a cache entry is expired
 * @param {Object} entry - Cache entry
 * @returns {boolean} True if expired, false otherwise
 */
function isExpired(entry) {
	if (!entry || !entry.timestamp || !entry.ttl) {return true;}
	return Date.now() > entry.timestamp + entry.ttl;
}

/**
 * Retrieve cache entry from storage
 * @param {string} key - Cache key
 * @returns {Object|null} Cache entry or null if not found/error
 */
function getFromCache(key) {
	try {
		return get(key);
	} catch (e) {
		console.error('Sidekick cache read error:', e);
		return null;
	}
}

/**
 * Clear all configuration cache
 * @param {Object} metadata - Metadata about the cache clearing
 */
function clearCache(metadata) {
	if (!metadata) { metadata = {}; }
	[sessionStorage, localStorage].forEach((storage) => {
		Object.keys(storage).forEach((key) => {
			if (key.startsWith(`${ns}CONFIG_`)) {storage.removeItem(key);}
		});
	});
	// Emit invalidation event; metadata may already contain a reason field
	events.emit('config-cache.invalidated', metadata);
}

/**
 * Store cache entry in storage
 * @param {string} storageType - 'local' or 'session'
 * @param {string} key - Cache key
 * @param {Object} entry - Cache entry
 */
function storeToCache(storageType, key, entry) {
	try {
		const bytes = new TextEncoder().encode(JSON.stringify(entry)).length;
		if (bytes > CACHE_CONFIG.MAX_SIZE) {
			console.warn(
				'Sidekick: Config data exceeds cache size limit (bytes=',
				bytes,
				')'
			);
			return;
		}

		// Clear any existing config cache entries for different organizations
		// to ensure we only store data for the current org context
		[sessionStorage, localStorage].forEach((storage) => {
			Object.keys(storage).forEach((storageKey) => {
				if (storageKey.startsWith(`${ns}CONFIG_`) && storageKey !== `${ns}${key}`) {
					storage.removeItem(storageKey);
				}
			});
		});

        const expiresInMinutes = entry.ttl / (60 * 1000);
        set(key, entry, { type: storageType, expires: expiresInMinutes });
    } catch (e) {
		console.error('Sidekick cache write error:', e);
		if (e.name === 'QuotaExceededError') {
			clearCache({ reason: 'quota-exceeded' });
		}
    }
}

/**
 * Fetch configuration data and store in cache
 * @param {string} endpoint - The config API endpoint
 * @param {Function} fetchFn - Function to fetch config from API
 * @param {string} cacheKey - Cache key
 * @param {string} storageType - 'local' or 'session'
 * @param {number} ttl - Time to live in milliseconds
 * @returns {Promise<Object>} Configuration data
 */
async function fetchAndStore(endpoint, fetchFn, cacheKey, storageType, ttl) {
	try {
		const configData = await fetchFn();
		const cacheEntry = {
			data: configData,
			timestamp: Date.now(),
			ttl,
			endpoint,
		};
		storeToCache(storageType, cacheKey, cacheEntry);
		return configData;
	} catch (error) {
		const staleCache = getFromCache(cacheKey);
		if (staleCache) {
			console.warn('Sidekick: Using stale cache due to fetch error');
			return staleCache.data;
		}
		throw error;
	}
}

/**
 * Get cached configuration data or fetch from API
 * @param {string} endpoint - The config API endpoint
 * @param {Function} fetchFn - Function to fetch config from API
 * @param {string} [storageType] - Storage type: 'local' (default) or 'session'
 * @param {number} [ttl] - Time to live in milliseconds (default: 30 minutes)
 * @returns {Promise<Object>} Configuration data
 */
async function getConfig(
	endpoint,
	fetchFn,
	storageType = CACHE_CONFIG.DEFAULT_STORAGE,
	ttl = CACHE_CONFIG.DEFAULT_TTL
) {
	if (!_isInitialized) {setupInvalidationListeners();}
	if (typeof fetchFn !== 'function') {
		console.error('Sidekick: fetchFn is not a function');
		return null;
	}
	_lastFetchParams = { endpoint, fetchFn, storageType, ttl };
	const cacheKey = createCacheKey(endpoint);
	const cached = getFromCache(cacheKey);
	if (cached && !isExpired(cached)) {
		return cached.data;
	}

	const cachedData = getCachedData(cacheKey);
	// Emit event if cache was expired
    if (cachedData && isExpired(cachedData)) {
        events.emit('config-cache.invalidated', { reason: 'cache-expired' });
    }
	return fetchAndStore(endpoint, fetchFn, cacheKey, storageType, ttl);
}

/**
 * Refresh the configuration cache
 * Clears the cache and re-fetches the configuration
 * @param {string} reason - Reason for the refresh
 * @param {Object} [metadata] - Additional metadata
 */
function refreshCache(reason, metadata) {
	if (!metadata) { metadata = {}; }
	if (_isRefreshing) {
		return;
	}
	_isRefreshing = true;

	// Only clear cache if entries exist
	const stats = computeCacheStats();
	if (stats && stats.totalEntries > 0) {
		clearCache(Object.assign({ reason: reason }, metadata));
	}

	if (!_lastFetchParams) {
		_isRefreshing = false;
		return;
	}
	const { endpoint, fetchFn, storageType, ttl } = _lastFetchParams;
	Promise.resolve()
		.then(() => getConfig(endpoint, fetchFn, storageType, ttl))
		.then((configData) => {
			const isSupersuitUiModule = window.buildConfig && window.buildConfig.isSupersuitUiModule;
			store.set('sk-config-data', configData);
			if (!isSupersuitUiModule && configData) {injectData(configData);}
			events.emit('config-cache.refreshed', Object.assign({ reason: reason }, metadata));
		})
		.catch((error) => {
			console.error('Sidekick: Failed to refresh cache', error);
			events.emit('config-cache.refresh-failed', {
				reason: reason,
				error: error.message,
			});
		})
		.finally(() => {
			_isRefreshing = false;
		});
}

/**
 * Compute cache statistics
 * @returns {Object} Cache statistics
 */
function computeCacheStats() {
	const sessionKeys = Object.keys(sessionStorage).filter((k) =>
		k.startsWith(`${ns}CONFIG_`)
	);
	const localKeys = Object.keys(localStorage).filter((k) =>
		k.startsWith(`${ns}CONFIG_`)
	);
	return {
		sessionStorageEntries: sessionKeys.length,
		localStorageEntries: localKeys.length,
		totalEntries: sessionKeys.length + localKeys.length,
		defaultStorage: CACHE_CONFIG.DEFAULT_STORAGE,
	};
}

// Initialize cache invalidation listeners
function initListeners() {
	setupInvalidationListeners();
}

// The object that exposes the private API for config cache management
export const configCacheAPI = {
	getConfig,
	clearCache,
	refreshCache,
	getCacheStats: computeCacheStats,
	initListeners,
};

// --------------- Public API (mirrors previous exports) ---------------

/**
 * Get cached configuration data or fetch from API
 * @param {string} endpoint - The config API endpoint
 * @param {Function} fetchFn - Function to fetch config from API
 * @param {string} [storageType] - Storage type: 'local' (default) or 'session'
 * @param {number} [ttl] - Time to live in milliseconds (default: 30 minutes)
 * @returns {Promise<Object>} Configuration data
 */
export const getCachedConfig = (
	endpoint,
	fetchFn,
	storageType = CACHE_CONFIG.DEFAULT_STORAGE,
	ttl = CACHE_CONFIG.DEFAULT_TTL
) => configCacheAPI.getConfig(endpoint, fetchFn, storageType, ttl);

/**
 * The object that exposes the public API for config cache management
 */
export const publicApi = {
	clearConfigCache: clearCache,
	refreshConfigCache: refreshCache
};
